import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, Navigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import * as Dialog from '@radix-ui/react-dialog'
import { Loader2, Trash2, Eye, X } from 'lucide-react'
import { toast } from 'sonner'
import { supabase } from '@/lib/supabaseClient'
import { usePlatformRoleContext } from '@/hooks/usePlatformRoleContext'
import { formatDate } from '@/lib/utils'

interface BriefRow {
  id: string
  services: string[]
  discovery_meeting_only: boolean
  company_goals: string | null
  budget: string | null
  timeline: string | null
  additional_notes: string | null
  marketing_opt_in: boolean
  status: string
  created_at: string
  business_id: string | null
  businesses: { id: string; name: string } | null
  people: { first_name: string; last_name: string; email: string } | null
}

const STATUS_LABELS: Record<string, string> = {
  new: 'New',
  contacted: 'Contacted',
  in_progress: 'In progress',
  won: 'Won',
  lost: 'Lost',
}

export default function PortalBriefs() {
  const { canSeeClients, isLoading: roleLoading, isError: roleError } = usePlatformRoleContext()
  const queryClient = useQueryClient()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [detailBrief, setDetailBrief] = useState<BriefRow | null>(null)

  const { data: briefs, isLoading: briefsLoading } = useQuery({
    queryKey: ['admin-project-briefs'],
    queryFn: async (): Promise<BriefRow[]> => {
      const { data, error } = await supabase
        .from('project_briefs')
        .select(`
          id,
          services,
          discovery_meeting_only,
          company_goals,
          budget,
          timeline,
          additional_notes,
          marketing_opt_in,
          status,
          created_at,
          business_id,
          businesses(id, name),
          people(first_name, last_name, email)
        `)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as unknown as BriefRow[]
    },
    enabled: canSeeClients,
  })

  const handleUpdateStatus = async (id: string, status: string) => {
    setEditingId(id)
    try {
      const { error } = await supabase
        .from('project_briefs')
        .update({ status, updated_at: new Date().toISOString() })
        .eq('id', id)
      if (error) throw error
      await queryClient.invalidateQueries({ queryKey: ['admin-project-briefs'] })
      toast.success('Project brief updated')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to update')
    } finally {
      setEditingId(null)
    }
  }

  const handleDelete = async (id: string) => {
    setDeletingId(id)
    try {
      const { error } = await supabase.from('project_briefs').delete().eq('id', id)
      if (error) throw error
      await queryClient.invalidateQueries({ queryKey: ['admin-project-briefs'] })
      toast.success('Project brief deleted')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed to delete')
    } finally {
      setDeletingId(null)
    }
  }

  if (roleLoading) {
    return (
      <div className="flex justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-white/70" />
      </div>
    )
  }

  if (roleError) {
    return (
      <div className="max-w-md mx-auto py-16 text-center">
        <p className="text-white/80">Unable to verify your access.</p>
      </div>
    )
  }

  if (!canSeeClients) {
    return <Navigate to="/portal" replace />
  }

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        <h1 className="text-2xl md:text-3xl font-heading font-bold text-white">Project briefs</h1>
        <p className="text-white/60 text-sm mt-1">All project briefs submitted via the new project form.</p>
      </motion.div>

      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3, delay: 0.05 }}
        className="rounded-xl border border-white/15 bg-white/5 overflow-hidden backdrop-blur-sm"
      >
        {briefsLoading ? (
          <div className="flex justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-white/70" />
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="border-b border-white/15">
                  <th className="px-4 py-3 text-xs font-heading uppercase tracking-wide text-white/60">Contact</th>
                  <th className="px-4 py-3 text-xs font-heading uppercase tracking-wide text-white/60">Business</th>
                  <th className="px-4 py-3 text-xs font-heading uppercase tracking-wide text-white/60">Summary</th>
                  <th className="px-4 py-3 text-xs font-heading uppercase tracking-wide text-white/60">Status</th>
                  <th className="px-4 py-3 text-xs font-heading uppercase tracking-wide text-white/60">Created</th>
                  <th className="px-4 py-3 text-right text-xs font-heading uppercase tracking-wide text-white/60">Actions</th>
                </tr>
              </thead>
              <tbody>
                {(briefs ?? []).length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-4 py-8 text-center text-white/50 text-sm">
                      No project briefs yet.
                    </td>
                  </tr>
                ) : (
                  (briefs ?? []).map((b) => (
                    <tr
                      key={b.id}
                      className="border-b border-white/10 last:border-0 hover:bg-white/5 transition-colors"
                    >
                      <td className="px-4 py-3">
                        <div className="text-white font-medium">
                          {b.people
                            ? `${b.people.first_name} ${b.people.last_name}`.trim() || b.people.email
                            : '—'}
                        </div>
                        {b.people?.email && (
                          <div className="text-xs text-white/60">{b.people.email}</div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {b.businesses ? (
                          <Link
                            to={`/portal/businesses/${b.businesses.id}`}
                            className="text-white/90 hover:text-white hover:underline"
                          >
                            {b.businesses.name}
                          </Link>
                        ) : (
                          <span className="text-white/50">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-white/80 max-w-xs truncate">
                        {b.discovery_meeting_only
                          ? 'Discovery meeting only'
                          : b.services.length > 0
                            ? b.services.join(', ')
                            : b.company_goals?.slice(0, 60) ?? '—'}
                        {(b.company_goals && !b.discovery_meeting_only && b.services.length > 0) ? '…' : ''}
                      </td>
                      <td className="px-4 py-3">
                        <select
                          value={b.status}
                          onChange={(e) => handleUpdateStatus(b.id, e.target.value)}
                          disabled={editingId === b.id}
                          className="select-chevron-sm text-xs font-heading uppercase tracking-wide px-2 py-1 rounded border border-white/20 bg-white/5 text-white focus:outline-none focus:border-white/40 disabled:opacity-50"
                        >
                          {Object.entries(STATUS_LABELS).map(([val, lbl]) => (
                            <option key={val} value={val} className="bg-primary text-white">
                              {lbl}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="px-4 py-3 text-white/70 text-sm">{formatDate(b.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="flex items-center justify-end gap-1">
                          <button
                            type="button"
                            onClick={() => setDetailBrief(b)}
                            className="p-1.5 rounded text-white/50 hover:text-white hover:bg-white/5 transition-colors"
                            aria-label="View details"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            type="button"
                            onClick={() => window.confirm('Delete this project brief?') && handleDelete(b.id)}
                            disabled={deletingId === b.id}
                            className="p-1.5 rounded text-white/50 hover:text-red-400 hover:bg-white/5 transition-colors disabled:opacity-50"
                            aria-label="Delete"
                          >
                            {deletingId === b.id ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              <Trash2 className="h-4 w-4" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </motion.div>

      <Dialog.Root open={!!detailBrief} onOpenChange={(open) => !open && setDetailBrief(null)}>
        <Dialog.Portal>
          <Dialog.Overlay className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
          <Dialog.Content className="fixed left-1/2 top-1/2 z-50 w-full max-w-2xl max-h-[90vh] -translate-x-1/2 -translate-y-1/2 rounded-xl border border-white/15 bg-slate-900 shadow-xl overflow-hidden flex flex-col data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0">
            {detailBrief && (
              <>
                <div className="flex items-center justify-between px-6 py-4 border-b border-white/15">
                  <Dialog.Title className="text-lg font-semibold text-white">
                    Brief details — {detailBrief.people
                      ? `${detailBrief.people.first_name} ${detailBrief.people.last_name}`.trim() || detailBrief.people.email
                      : 'Unknown'}
                  </Dialog.Title>
                  <Dialog.Close asChild>
                    <button
                      type="button"
                      className="p-2 rounded text-white/50 hover:text-white hover:bg-white/5 transition-colors"
                      aria-label="Close"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </Dialog.Close>
                </div>
                <div className="flex-1 overflow-y-auto px-6 py-4 space-y-6">
                  <DetailSection title="Contact">
                    <p className="text-white/90">
                      {detailBrief.people
                        ? `${detailBrief.people.first_name} ${detailBrief.people.last_name}`.trim() || detailBrief.people.email
                        : '—'}
                    </p>
                    {detailBrief.people?.email && (
                      <p className="text-sm text-white/60 mt-0.5">{detailBrief.people.email}</p>
                    )}
                  </DetailSection>
                  <DetailSection title="Business">
                    {detailBrief.businesses ? (
                      <Link
                        to={`/portal/businesses/${detailBrief.businesses.id}`}
                        className="text-white/90 hover:text-white hover:underline"
                      >
                        {detailBrief.businesses.name}
                      </Link>
                    ) : (
                      <span className="text-white/50">—</span>
                    )}
                  </DetailSection>
                  <DetailSection title="Status">
                    <span className="text-xs font-heading uppercase tracking-wide px-2 py-0.5 rounded border border-white/20 bg-white/5 text-white">
                      {STATUS_LABELS[detailBrief.status] ?? detailBrief.status}
                    </span>
                  </DetailSection>
                  <DetailSection title="Services">
                    {detailBrief.discovery_meeting_only ? (
                      <p className="text-white/80">Discovery meeting only</p>
                    ) : detailBrief.services.length > 0 ? (
                      <p className="text-white/80">{detailBrief.services.join(', ')}</p>
                    ) : (
                      <span className="text-white/50">—</span>
                    )}
                  </DetailSection>
                  {detailBrief.company_goals && (
                    <DetailSection title="Company goals">
                      <p className="text-white/80 whitespace-pre-wrap">{detailBrief.company_goals}</p>
                    </DetailSection>
                  )}
                  {detailBrief.budget && (
                    <DetailSection title="Budget">
                      <p className="text-white/80">{detailBrief.budget}</p>
                    </DetailSection>
                  )}
                  {detailBrief.timeline && (
                    <DetailSection title="Timeline">
                      <p className="text-white/80 whitespace-pre-wrap">{detailBrief.timeline}</p>
                    </DetailSection>
                  )}
                  {detailBrief.additional_notes && (
                    <DetailSection title="Additional notes">
                      <p className="text-white/80 whitespace-pre-wrap">{detailBrief.additional_notes}</p>
                    </DetailSection>
                  )}
                  <DetailSection title="Marketing opt-in">
                    <p className="text-white/80">{detailBrief.marketing_opt_in ? 'Yes' : 'No'}</p>
                  </DetailSection>
                  <DetailSection title="Submitted">
                    <p className="text-white/70 text-sm">{formatDate(detailBrief.created_at)}</p>
                  </DetailSection>
                </div>
              </>
            )}
          </Dialog.Content>
        </Dialog.Portal>
      </Dialog.Root>
    </div>
  )
}

function DetailSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h3 className="text-xs font-heading uppercase tracking-wide text-white/50 mb-1.5">{title}</h3>
      {children}
    </div>
  )
}
